import {
  type ClientEvent,
  CodeResponseSchema,
  ConversationListSchema,
  ConversationSnapshotSchema,
  EventResponseSchema,
  MessagePageSchema,
  StartConversationResponseSchema,
} from '@oneapp/shared';
import { z } from 'zod';
import { api } from '../lib/api';

/** The chat API (see packages/shared/src/api.ts). Every call is made as the signed-in person. */
export const chatApi = {
  code: (challengeId: string, code: string, clientId: string, from: 'page' | 'box') =>
    api.post(`/api/v1/codes/${encodeURIComponent(challengeId)}`, CodeResponseSchema, {
      auth: true,
      body: { code, from },
      idempotencyKey: clientId,
    }),
  /** The latest conversation, or a new one ("New chat" always starts one). */
  start: (fresh = false) =>
    api.post('/api/v1/conversations', StartConversationResponseSchema, { auth: true, body: { fresh } }),

  snapshot: (id: string, signal?: AbortSignal) =>
    api.get(`/api/v1/conversations/${encodeURIComponent(id)}`, ConversationSnapshotSchema, {
      auth: true,
      ...(signal ? { signal } : {}),
    }),

  older: (id: string, beforeSeq: number) =>
    api.get(
      `/api/v1/conversations/${encodeURIComponent(id)}/messages?before=${beforeSeq}&limit=50`,
      MessagePageSchema,
      {
        auth: true,
      },
    ),

  /** `clientId` is the Idempotency-Key: sending the same event again is handled once. */
  send: (id: string, event: ClientEvent, clientId: string) =>
    api.post(`/api/v1/conversations/${encodeURIComponent(id)}/events`, EventResponseSchema, {
      auth: true,
      body: event,
      idempotencyKey: clientId,
    }),

  list: (before?: string | null) =>
    api.get(
      `/api/v1/conversations?limit=20${before ? `&before=${encodeURIComponent(before)}` : ''}`,
      ConversationListSchema,
      { auth: true },
    ),

  remove: (id: string) => api.delete(`/api/v1/conversations/${encodeURIComponent(id)}`, z.null(), { auth: true }),
};
